import { Web3Provider } from '@ethersproject/providers'
import { Connector, Provider, Web3ReactStore } from '@web3-react/types'
import {
  AddChainParameter,
  ExtendedChainInformation,
  NetworkListItem,
  getAddChainParams,
  getDefaultChainId,
  isValidChain
} from 'lib/web3/chains'
import isEqual from 'lodash/isEqual'
import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { toast } from 'components/common/Toast'

import { CHAIN_LIST, Network, TokenOptions, TokenSymbol } from '@nsfw-app/crypto'
import { SnackBar } from 'common/SnackBar'
import snackBar from 'common/SnackBar/config'
import { formatAddress } from 'common/Web3/formatAddress'
import { CHAINS, CONNECTED_WALLET, mapChainToNetwork } from 'config'
import { ErrorCodes } from 'config/errorCodes'
import { getTokensForChainId } from 'config/tokens'
import { SUPPORTED_WALLETS, Wallet, WalletInfo } from 'config/wallets'
import { BigNumber } from 'ethers'
import { LINK_WALLET_MUTATION } from 'graphql/mutations/linkWallet'
import { LinkWalletInput, MutationLinkWalletArgs, WalletProvider } from 'graphql/types'
import { isBrowser, isServer } from 'lib/nextjs'
import { hasErrorCode } from 'lib/urql/errors'
import { mutation } from 'lib/urql/mutation'
import { requestSignature } from 'lib/web3/signatures/requestSignature'
import { Anchor } from 'primitives/Anchor'
import { AppBannerKeys } from 'shared/Banner/types'
import { RootStore } from './RootStore'

export type TransactionHash = string
export type EthAddress = string
export type ChainHex = string

export class InjectedWallet {
  root: RootStore

  connector?: Connector = undefined
  provider?: Web3Provider = undefined
  activating = false

  CHAINS: typeof CHAIN_LIST = CHAIN_LIST

  defaultChainId = getDefaultChainId()

  balances = new Map<TokenSymbol, BigNumber>()

  /**
   * Used to control when to sync balances
   */
  syncedBalances = false

  /**
   * @deprecated
   */
  private _selectedWallet?: Wallet = undefined

  /**
   * @deprecated
   */
  private _chainId?: number = undefined

  /**
   * @deprecated
   */
  private _accounts: string[] = []

  // Sync internal web3-react Zustand store with Mobx
  private _store?: Web3ReactStore = undefined

  // The currently selected network state (application network selection, not wallet)
  private _selectedNetworkId?: number = undefined

  constructor(root: RootStore) {
    this.root = root

    makeAutoObservable(this)

    // Accounts can change from wallets (i.e metamask extension), this ensures
    // reconnection and link checking
    reaction(
      () => this.accounts,
      async (accounts) => {
        console.debug('accounts list changed', JSON.stringify(accounts))

        if (accounts.length) {
          if (!this.isActive) await this.connectEagerly()
          if (this.root.user.isLoggedIn) {
            if (this.isLinked) {
              this.root.ui.closeConnectModal()
              this.root.ui.hideBanner(AppBannerKeys.LINK_WALLET)
            } else {
              // We only care about linking wallets for fans now
              // if (!this.root.user.isCreator) {
              //   this.root.ui.displayBanner(
              //     AppBannerKeys.LINK_WALLET,
              //     'Please link a wallet to pay creators.',
              //     <Anchor
              //       theme="text"
              //       css={{ color: '$gold100' }}
              //       onClick={() => this.root.ui.openConnectModal('LINK_WALLET')}
              //     >
              //       Link wallet
              //     </Anchor>
              //   )
              // }
            }
          }
        }
      }
    )

    // When the connectors provider is re/initialised, attempt to eagerly reconnect
    // reaction(
    //   () => this.provider,
    //   (provider) => {
    //     if (provider && !this.isActive) {
    //       this.connectEagerly()
    //     }
    //   }
    // )

    // When a valid chainId is set, update the selectedNetwork (for NetworkSelector display).
    // We need this as there is not response when a user cancels a switch network wallet request.
    // We only want to display the selected network when its actually selected in the wallet.
    // reaction(
    //   () => this.chainId,
    //   (chainId) => {
    //     if (chainId && isValidChain(chainId)) {
    //       this.setSelectedNetwork(chainId)
    //       this.root.transaction.setToken(this.defaultTokenForNetwork)
    //     } else if (this._selectedNetworkId) {
    //       this.setSelectedNetwork(this._selectedNetworkId)
    //       // If user switches to an invalid network, do we want to prompt the wallet directly to switch address?
    //       // this.switchNetwork(this._selectedNetworkId)
    //     } else this.setSelectedNetwork(this.defaultChainId)
    //   }
    // )

    // reaction(
    //   () => this.root.user.isLoggedIn,
    //   async (isLoggedIn) => {
    //     if (!this.isActive) await this.connectEagerly()
    //     if (isLoggedIn && !this.root.user.isCreator && !this.isLinked) {
    //       const msg = 'Please link a wallet to pay creators.'
    //       this.root.ui.displayBanner(
    //         AppBannerKeys.LINK_WALLET,
    //         msg,
    //         <Anchor
    //           theme="text"
    //           css={{ color: '$gold100' }}
    //           onClick={() => this.root.ui.openConnectModal('LINK_WALLET')}
    //         >
    //           Link wallet
    //         </Anchor>
    //       )
    //     }
    //   },
    //   { fireImmediately: isBrowser() }
    // )
  }

  // https://github.com/NoahZinsmeister/web3-react/blob/%40web3-react/core%408.0.33-beta.0/packages/core/src/hooks.ts#L277
  get isActive() {
    return Boolean(this.chainId && this.accounts.length && !this.activating)
  }

  get connectedWallet() {
    return this.root.user.privyUser?.wallet
  }

  get chainId() {
    return this._chainId
  }

  /**
   * @deprecated
   */
  get connectedAddress() {
    return this.root.wallets.connected?.address
  }

  get truncatedAddress() {
    return formatAddress(this.connectedAddress)
  }

  get accounts() {
    return this._accounts
  }

  get selectedWallet() {
    return this._selectedWallet
  }

  get apiWalletProvider() {
    switch (this.selectedWallet) {
      case Wallet.METAMASK:
        return WalletProvider.BROWSER_EXTENSION
      case Wallet.WALLET_CONNECT:
        return WalletProvider.WALLETCONNECT
      default:
        return undefined
    }
  }

  get walletBrandName() {
    if (!this.selectedWallet) return undefined

    return SUPPORTED_WALLETS[this.selectedWallet]?.name
  }

  get supportedWallets() {
    const isMobile = this.root.ui.isMobile
    return Object.keys(SUPPORTED_WALLETS)
      .map((key) => SUPPORTED_WALLETS[key as Wallet])
      .filter((o: WalletInfo) => (isMobile && o.mobile) || (!isMobile && !o.mobileOnly))
  }

  // We want to received updates from this computed observable when auth status or selected
  // wallet account changes. Accounts can be connected in state prior to login or link, and accounts
  // can be changed via external wallets with or without valid auth.
  get isLinked() {
    return this.root.user.isWalletRegistered(this.connectedAddress)
  }

  get isCurrentChainValid() {
    return isValidChain(this.chainId)
  }

  private set chainId(chainId: number | undefined) {
    this._chainId = chainId
    // Provider needs to be updated when wallet is connected, and when the network (chainId) changes.
    // Both those events cause the web3-react state chainId to update, and therefore this setter to get called.
    // https://github.com/NoahZinsmeister/web3-react/blob/%40web3-react/core%408.0.33-beta.0/packages/core/src/hooks.ts#L340
    if (chainId && this.connector && this.provider?.network?.chainId !== chainId) {
      this._initWeb3Provider(this.connector)
    }
  }

  private set accounts(accounts: string[]) {
    this._accounts = accounts
  }

  // Set the wallet and initialise the connect from wallet.
  // Use .connect(wallet) / .disconnect() as a public method to set/unset a wallet.
  private set selectedWallet(wallet: Wallet | undefined) {
    console.log('selectedWallet()', wallet)
    console.log('selectedWallet()', wallet)
    console.log('selectedWallet()', wallet)
    // this._selectedWallet = wallet
    // if (wallet) {
    //   const { connector, store } = getConnectorListItemForWallet(wallet)
    //   this.connector = connector
    //   this.store = store
    //   localStorage.setItem(CONNECTED_WALLET, wallet)
    // } else {
    //   this.connector = undefined
    //   this.chainId = undefined
    //   this.accounts = []
    //   localStorage.removeItem(CONNECTED_WALLET)
    // }
  }

  private set store(s: Web3ReactStore) {
    this._store = s
    // Sync internal web3-react Zustand store with Mobx
    this._store.subscribe((state) => {
      console.debug('internal web3 connector state', state)
      runInAction(() => {
        // web3 store subscribe can get invoke with same values, we want to reduce observer
        // notifications for same values
        if (state.chainId !== this.chainId) this.chainId = state.chainId
        if (!isEqual(state.accounts, this.accounts)) this.accounts = state.accounts ?? []
        if (state.activating !== this.activating) this.activating = state.activating
      })
    })
  }

  getWalletFromStorage() {
    return localStorage.getItem(CONNECTED_WALLET) as Wallet | undefined
  }

  setSelectedNetwork(id?: number | string) {
    if (id) this._selectedNetworkId = Number(id)
    return this.selectedNetwork
  }

  // Connects to existing wallet connection
  async connectEagerly() {
    console.log('connectEagerly() — TODO')
    console.log('connectEagerly() — TODO')
    console.log('connectEagerly() — TODO')

    if (isServer()) return

    // try {
    //   if (!this.selectedWallet) {
    //     const cachedWallet = this.getWalletFromStorage()
    //     if (isValidWallet(cachedWallet)) {
    //       this.selectedWallet = cachedWallet
    //     }
    //   }

    //   if (this.connector?.connectEagerly) {
    //     // Fails silently
    //     // https://github.com/NoahZinsmeister/web3-react/blob/%40web3-react/metamask%408.0.26-beta.0/packages/metamask/src/index.ts#L87
    //     await this.connector.connectEagerly()
    //   }
    // } catch (error) {
    //   console.debug('Unable to eagerly connect', error)
    // }
  }

  // Initialise a connect to a wallet
  // - will launch modal for WALLET_CONNECT, so only call on user action
  // - this.eagerlyConnect should be called before attempting to initialise a new connection
  /**
   * @deprecated
   */
  // async connect(wallet: Wallet) {
  //   if (isServer()) return

  //   console.debug('Attempting manual connection', wallet, this.isActive)
  //   if (!(wallet && isValidWallet(wallet))) {
  //     throw new Error(`Invalid wallet "${wallet}"`)
  //   }

  //   // Already connected to given wallet
  //   if (wallet === this.selectedWallet && this.isActive) {
  //     return
  //   }

  //   try {
  //     // Set wallet, which initialises the connector & subscribes to the web3-react store
  //     this.selectedWallet = wallet
  //     this.root.analytics.track(NSFW_EVENT.WALLET_CONNECT_SELECT, {
  //       walletType: wallet
  //     })

  //     // Initialise the connection, important to attempt eagerly first as activating an already
  //     // active connector throws an error for metamask.
  //     await this.connector!.activate()
  //     this.root.analytics.track(NSFW_EVENT.WALLET_CONNECTED, {
  //       walletType: this.selectedWallet,
  //       chainId: this.chainId
  //     })
  //   } catch (error) {
  //     console.error('web3 connect error: ', error)
  //     throw new Error('Error connecting wallet')
  //   }
  // }

  async disconnect(withToast = true) {
    if (!this.connector) return

    try {
      console.debug('Attempting disconnect wallet connector')
      await (this.connector.deactivate ? this.connector.deactivate() : this.connector.resetState())

      this.selectedWallet = undefined

      if (withToast) {
        // toast(() => <SnackBar isSuccess title={'Wallet disconnected'} />, snackBar)
      }
    } catch (err) {
      // toast(() => <SnackBar isError title={'Failed to disconnect wallet'} />, snackBar)
    }
  }

  /**
   * @deprecated
   */
  // async fetchNonce() {
  //   const { data, error } = await query<{ nonce: string }>(QUERY_NONCE)
  //   if (!data?.nonce || error) {
  //     toast(() => <SnackBar isError title="Error fetching nonce" />, snackBar)
  //     throw error || new Error('No data found for query Nonce')
  //   }
  //   return data.nonce
  // }

  // Nonce must be provided as RPC calls must be the first async request after user action
  // to work on mobile for app deep links.
  async signWalletData(nonce: string, userId?: string) {
    if (!this.connectedAddress) throw new Error('No account connect to link')
    if (!this.provider) throw new Error('No provider instance')
    if (!this.chainId) throw new Error('No chainId selected')
    if (!(this.walletBrandName && this.apiWalletProvider)) {
      throw new Error('No valid wallet selected')
    }

    const signature = await requestSignature({
      chainId: this.chainId,
      signerAddress: this.connectedAddress,
      provider: this.provider,
      userId,
      nonce
    })
    return {
      title: this.walletBrandName,
      provider: this.apiWalletProvider,
      chainId: this.chainId,
      address: this.connectedAddress,
      signature,
      nonce
    }
  }

  async link(input: LinkWalletInput) {
    const { error } = await mutation<{ linkWallet: boolean }, MutationLinkWalletArgs>(
      LINK_WALLET_MUTATION,
      { input }
    )

    if (error) {
      console.log(error)
      if (hasErrorCode(ErrorCodes.WALLET_IN_USE, error)) {
        this.root.ui.openConnectModal('ALREADY_LINKED')
        return false
      }

      // toast(
      //   () => (
      //     <SnackBar isError title={'Error linking wallet. Please try again or contact support.'} />
      //   ),
      //   snackBar
      // )
      return false
    }

    // Fetch latest wallet data to check user.isWalletLinked()
    this.root.user.fetchWallets().catch((error) =>
      // Track error in Sentry, not fatal, use will probably not use the metamask extension
      // wallet switching, and page refresh would resolve the issue
      console.error('Error fetching user account after wallet link', error)
    )

    this.root.ui.hideBanner(AppBannerKeys.LINK_WALLET)

    // toast(() => <SnackBar isSuccess title={'Wallet successfully linked'} />, snackBar)
    return true
  }

  // To prevent bouncing between nsfw and wallet connect app for switch then add, we can
  // just call add network directly, which will natively prompt the user for switching afterwards.
  // If the network is already added it will just request to switch.
  // --
  // https://docs.metamask.io/guide/rpc-api.html#wallet-addethereumchain
  async switchNetwork(chainId: Network) {
    if (!(this.connector && this.provider)) {
      throw new Error('Attempted to switch network without a connector or provider instance')
    }
    if (!isValidChain(chainId)) {
      console.error('Invalid chainId')
      // toast(() => <SnackBar isError title={'Invalid chain'} />, snackBar)
      return
    }

    const params = getAddChainParams(chainId)

    const switchNetworkFunctions = [
      async (provider: Web3Provider, params: AddChainParameter) => {
        await provider.send('wallet_switchEthereumChain', [params])
      },
      async (provider: Web3Provider, params: AddChainParameter) => {
        await provider.send('wallet_addEthereumChain', [params])
      }
    ]

    for await (const func of switchNetworkFunctions) {
      try {
        await func(this.provider, params)
        return
      } catch (err: any) {
        if (err.code === -32002) {
          // toast(
          //   () => (
          //     <SnackBar
          //       isError
          //       title={'You have an existing request. Please check your wallet. '}
          //     />
          //   ),
          //   snackBar
          // )
        }
        console.error(err)
      }
    }
  }

  get defaultTokenForNetwork() {
    const defaultToken = getTokensForChainId(this.selectedNetwork.id).filter(
      (token: TokenOptions) => {
        return token.isDefault
      }
    )
    return defaultToken[0]
  }

  get selectedNetwork() {
    const id = this._selectedNetworkId || this.chainId || this.defaultChainId

    // if user switches to invalid network and is connected and refreshes the page
    if (this._selectedNetworkId === undefined && this.chainId && !isValidChain(this.chainId)) {
      return mapChainToNetwork(
        this.defaultChainId,
        CHAINS[this.defaultChainId] as ExtendedChainInformation
      )
    }
    return mapChainToNetwork(id, CHAINS[id] as ExtendedChainInformation)
  }

  /**
   * Get balances for all enabled tokens on the current network
   */
  async syncBalances() {
    const tokens = getTokensForChainId(this.selectedNetwork.id)
    await Promise.all(
      tokens
        .filter((o) => o.isEnabled)
        .map(async (token: TokenOptions) => {
          const address = this.root.wallets.connected?.address

          // Guards
          if (!address) throw new Error('No address to fetch balance')

          const balance = await this.root.erc20Actions
            .balanceOf(token.symbol, address)
            .catch((err) => {
              console.error(`Error fetching balance for ${token.symbol}`, err)
              return BigNumber.from(0)
            })

          this.balances.set(token.symbol, balance)
        })
    )

    this.setSyncedBalances(true)
  }

  setSyncedBalances(bool: boolean) {
    this.syncedBalances = bool
  }

  // TODO: review??
  getNetworksList(): NetworkListItem[] {
    return Object.entries(CHAINS)
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .map(([chainId, chain]) => ({
        id: chainId as unknown as Network,
        name: chain.name,
        symbol: chain.nativeCurrency.symbol
      }))
  }

  private _initWeb3Provider(connector: Connector) {
    // https://github.com/NoahZinsmeister/web3-react/blob/%40web3-react/core%408.0.33-beta.0/packages/core/src/hooks.ts#L340
    this.provider = new Web3Provider(connector.provider as Provider)
  }
}
